/**
 * Cursor transitions as pure functions.
 *
 * `(state, intent) → state`, with no key handling anywhere. That boundary is
 * not a preference: three consumer sessions chose it independently, and both
 * maintained ink scroll libraries are built the same way.
 *
 * EQStack's reason is the one with an incident behind it — their INPUT-GUARD
 * LAW (one top-level `useInput`, an early-return guard per modal mode) exists
 * because `q` typed into a recipient-name field once quit the app. A component
 * that owns keys either reimplements every consumer's modal routing or
 * reintroduces that bug for all of them at once.
 */
/**
 * Clamp to a valid index, or return null when the input is not a number at all.
 *
 * `null` rather than 0, because the two cases deserve different answers: an
 * out-of-range index should land at the nearest end, but a NaN index means the
 * caller's arithmetic broke, and teleporting the cursor to the top of the list
 * is the "cursor jumped 3,000 messages" bug report. Callers below preserve the
 * cursor instead. See `finite.ts` for the rule.
 */
const clampIndex = (i, itemCount) => {
    if (!Number.isFinite(i))
        return null;
    if (itemCount <= 0)
        return 0;
    return Math.min(Math.max(Math.floor(i), 0), itemCount - 1);
};
/** Pass finite values through; anything else stays NaN so `moved` holds position. */
const finiteOrNaN = (n) => (Number.isFinite(n) ? n : Number.NaN);
/** Resolve the follow-tail sentinel to a concrete index for movement. */
const concrete = (cursor, itemCount) => cursor < 0 ? Math.max(itemCount - 1, 0) : (clampIndex(cursor, itemCount) ?? 0);
export function navReduce(state, intent, ctx) {
    const { itemCount } = ctx;
    if (intent.kind === "itemsReplaced") {
        // THE SENTINEL IS NEVER REMAPPED. Following the tail is a relationship to
        // the end of the list, not to an index, so translating it is meaningless —
        // and doing so shipped as a real cursor-loss bug once (EQStack #94).
        if (state.cursor < 0)
            return state;
        // The remap's output is clamped HERE so a consumer whose remap points into
        // a removed region degrades to the nearest survivor. Callers should not
        // have to be defensive about their own eviction maths.
        const remapped = clampIndex(intent.remap(state.cursor), itemCount);
        // A remap that returns NaN is a broken caller, not a removed region. Hold
        // position rather than teleport.
        return remapped === null ? state : { ...state, cursor: remapped };
    }
    if (intent.kind === "digit") {
        const digit = Math.floor(intent.digit);
        if (digit < 0 || digit > 9)
            return state;
        return { ...state, count: (state.count ?? 0) * 10 + digit };
    }
    if (intent.kind === "set") {
        const target = clampIndex(intent.index, itemCount);
        return target === null ? state : { cursor: target, count: null, touched: true };
    }
    // Every remaining intent is a movement: it consumes the count as a repeat
    // factor, resets it, and marks the cursor touched.
    const repeat = Math.max(1, Number.isFinite(state.count ?? 1) ? (state.count ?? 1) : 1);
    const from = concrete(state.cursor, itemCount);
    // A movement that computes to NaN — a non-finite pageSize is the realistic
    // source — consumes the count but does not move. Landing on 0 would be a
    // visible teleport for what is really "this intent could not be evaluated".
    const moved = (cursor) => {
        const target = clampIndex(cursor, itemCount);
        return target === null
            ? { ...state, count: null }
            : { cursor: target, count: null, touched: true };
    };
    switch (intent.kind) {
        case "up":
            return moved(from - repeat);
        case "down":
            return moved(from + repeat);
        case "pageUp":
            return moved(from - finiteOrNaN(ctx.pageSize) * repeat);
        case "pageDown":
            return moved(from + finiteOrNaN(ctx.pageSize) * repeat);
        case "top":
            return moved(0);
        case "bottom":
            return moved(itemCount - 1);
        case "groupJump": {
            // No boundary function means the consumer has no group semantics. Doing
            // nothing is the honest response; throwing would punish a consumer for
            // wiring a key it happens not to support.
            if (!ctx.groupBoundary)
                return state;
            let at = from;
            for (let n = 0; n < repeat; n += 1)
                at = ctx.groupBoundary(at, intent.dir);
            return moved(at);
        }
    }
}
/**
 * What the cursor should be when a column's contents are replaced wholesale —
 * switching conversation, account, or folder.
 *
 * A PARAMETER, not a hardcode. The right default is contested inside a single
 * app: a conversation pane wants snap-to-tail, a file tree wants
 * restore-last-position, a log pane wants follow-until-touched. Any component
 * that picks one fights at least one of its consumers.
 */
export function applyRestore(policy, prev, itemCount) {
    switch (policy) {
        case "restore":
            return { ...prev, cursor: clampIndex(prev.cursor, itemCount) ?? 0, count: null };
        case "snap-end":
            return { cursor: clampIndex(itemCount - 1, itemCount) ?? 0, count: null, touched: false };
        case "snap-start":
            return { cursor: 0, count: null, touched: false };
        case "follow-until-touched":
            return prev.touched
                ? { ...prev, cursor: clampIndex(prev.cursor, itemCount) ?? 0, count: null }
                : { cursor: -1, count: null, touched: false };
    }
}
//# sourceMappingURL=nav-reduce.js.map